/**
 * FS-10 나의 발자취 계약(S5 — 제어 평면 C-07 · Bearer · 본인만):
 *   GET /api/courses/{c}/me/footprints → {versions:[{snapshotId, createdAt, title, modelSlug, glbUrl, appearance, current,
 *        attempts:[{attemptId, modeId, presetKey, outcome, durationMs, tries, failPattern, replayId, createdAt}]}],
 *        badges · classGoal(PH1 자리 — 쓰지 않는다: P2-S6 부터 …/me/achievements)}
 * 스냅샷 버전별(SR-REC-03 — 코드를 바꾸면 결과가 달라짐을 보이는 목적). 남의 기록·평균·순위 필드는 계약에 없다.
 * 셸은 모르는 필드를 버리고 모양이 어긋난 항목을 건너뛴다.
 */
import { useMemo } from "react";
import type { Appearance } from "@picnic-field/renderer/appearance";
import { useSession, useSessionManager } from "../../../store/session/SessionProvider";
import { BADGE_KEYS, GOAL_KEYS, badgeIcon, isBadgeKey } from "../lib/badges";
import type { Requester } from "../../../types/api";
import type {
  FootprintAttempt,
  FootprintVersion,
  Footprints,
  BadgeState,
  Achievements,
} from "../types";

const str = (v: unknown, d = ""): string => (typeof v === "string" ? v : d);

function readAttempt(v: unknown): FootprintAttempt | null {
  const o = (v ?? {}) as Record<string, unknown>;
  if (typeof o.attemptId !== "string" && typeof o.attemptId !== "number") return null;
  const outcome = o.outcome === "complete" ? "complete" : "incomplete";
  const d = Number(o.durationMs);
  const tries = Number(o.tries);
  return {
    attemptId: String(o.attemptId),
    modeId: str(o.modeId),
    presetKey: str(o.presetKey),
    outcome,
    durationMs: o.durationMs != null && Number.isFinite(d) ? d : null,
    tries: Number.isFinite(tries) && tries > 0 ? Math.round(tries) : 1,
    failPattern: typeof o.failPattern === "string" ? o.failPattern : null,
    replayId: typeof o.replayId === "string" && o.replayId ? o.replayId : null,
    createdAt: str(o.createdAt, new Date(0).toISOString()),
  };
}

/** 응답을 너그럽게 읽는다(모양이 어긋난 항목은 건너뛴다) */
export function readFootprints(v: unknown): Footprints {
  const o = (v ?? {}) as Record<string, unknown>;
  const versions = (Array.isArray(o.versions) ? o.versions : [])
    .map((x): FootprintVersion | null => {
      const r = (x ?? {}) as Record<string, unknown>;
      if (typeof r.snapshotId !== "string") return null;
      return {
        snapshotId: r.snapshotId,
        createdAt: str(r.createdAt, new Date(0).toISOString()),
        title: str(r.title, str(r.modelSlug)),
        modelSlug: str(r.modelSlug),
        glbUrl: typeof r.glbUrl === "string" ? r.glbUrl : null,
        appearance: (r.appearance as Appearance | null) ?? null,
        current: r.current === true,
        attempts: (Array.isArray(r.attempts) ? r.attempts : [])
          .map(readAttempt)
          .filter((a): a is FootprintAttempt => !!a),
      };
    })
    .filter((x): x is FootprintVersion => !!x);
  return { versions };
}

/**
 * 배지·반 목표 읽기 — 10종을 늘 다 그린다(서버가 빠뜨린 배지는 안 받은 것으로 · 모르는 키는 버린다).
 * 반 목표는 알려진 3종만 · 0..1(또는 0..100)을 0~100 정수로.
 */
export function readAchievements(v: unknown): Achievements {
  const o = (v ?? {}) as Record<string, unknown>;
  const got = new Map<string, Record<string, unknown>>();
  for (const b of Array.isArray(o.badges) ? o.badges : []) {
    const r = (b ?? {}) as Record<string, unknown>;
    if (isBadgeKey(r.key) && !got.has(r.key)) got.set(r.key, r);
  }
  const badges = BADGE_KEYS.map((key): BadgeState => {
    const r = got.get(key);
    const earned = r?.earned === true;
    return {
      key,
      icon: badgeIcon(key, typeof r?.icon === "string" ? r.icon : null),
      earned,
      earnedAt: earned && typeof r?.earnedAt === "string" ? r.earnedAt : null,
    };
  });
  const goals = new Map<string, number>();
  for (const g of Array.isArray(o.classGoals) ? o.classGoals : []) {
    const r = (g ?? {}) as Record<string, unknown>;
    const p = Number(r.progress);
    if (typeof r.key !== "string" || !Number.isFinite(p)) continue;
    const pct = p <= 1 ? p * 100 : p;
    // 내림(99.6% 를 100% 로 보이지 않게 — 부동소수 오차만 보정)
    goals.set(r.key, Math.max(0, Math.min(100, Math.floor(pct + 1e-9))));
  }
  const classGoals = GOAL_KEYS.filter((k) => goals.has(k)).map((key) => ({
    key,
    pct: goals.get(key)!,
  }));
  return { badges, classGoals };
}

export interface FootprintsApi {
  mine(): Promise<Footprints>;
  /** P2-S6 배지 · 반 공동 목표 */
  achievements(): Promise<Achievements>;
}

export function createFootprintsApi(request: Requester, courseId: string): FootprintsApi {
  const c = `/api/courses/${encodeURIComponent(courseId)}`;
  return {
    mine: async () => readFootprints(await request<unknown>(`${c}/me/footprints`)),
    achievements: async () => readAchievements(await request<unknown>(`${c}/me/achievements`)),
  };
}

export function useFootprintsApi(): FootprintsApi | null {
  const manager = useSessionManager();
  const state = useSession();
  const courseId = state.status === "ready" ? state.session.courseId : null;
  return useMemo(
    () => (courseId ? createFootprintsApi(manager.request, courseId) : null),
    [manager, courseId],
  );
}
